import React, { useEffect, useRef } from 'react';
import { Box, Button, LinearProgress, Typography, Alert } from '@mui/material';
import {
  useImportWizardStore,
  getPersistedJobState,
} from '../../store/useImportWizardStore';
import { leadImportService } from '../../services/leadImportService';
import { useCategories } from 'application/hooks/useCategories';
import { useJobPolling } from '../../hooks/useJobPolling';
import { calcProgress, isPollingEnabled } from '../../utils/progressHelpers';
import { isNotFoundError } from '../../utils/errorHelpers';
import ProgressStats from './ProgressStats';
import type { MappingConfig, CreateJobRequest } from '../../utils/types';

/**
 * ImportProgressStep handles:
 * 1. Creating a new import job (or recovering an existing one from localStorage)
 * 2. Polling job status at regular intervals
 * 3. Displaying progress bar and live counters
 * 4. Advancing to results step when job reaches terminal status
 */
const ImportProgressStep: React.FC = () => {
  const fileID = useImportWizardStore((s) => s.fileID);
  const mappingConfig = useImportWizardStore((s) => s.mappingConfig);
  const saveConfigName = useImportWizardStore((s) => s.saveConfigName);
  const jobID = useImportWizardStore((s) => s.jobID);
  const setJobID = useImportWizardStore((s) => s.setJobID);
  const setJobStatus = useImportWizardStore((s) => s.setJobStatus);
  const setStep = useImportWizardStore((s) => s.setStep);

  const { categories } = useCategories();
  const categoryID = mappingConfig?.categoryID ?? '';
  const selectedCategory = categories.find((cat) => cat.id === categoryID);
  const dealConfig = selectedCategory?.flexAttributes?.config?.deal;

  const jobCreatedRef = useRef(false);

  // On mount: check for persisted job state or create a new job
  useEffect(() => {
    if (jobCreatedRef.current) return;

    // Only use persisted state for recovery if we don't already have a fileID in the store
    const persisted = getPersistedJobState();
    const isWizardFlow = !!fileID && Object.keys(mappingConfig).length > 0;

    if (!isWizardFlow && persisted) {
      // Page refresh recovery: resume polling with persisted job ID
      setJobID(persisted.jobID);
      jobCreatedRef.current = true;
    } else if (fileID && mappingConfig && !jobID) {
      // Wait for categories to be loaded so we can get dealMappingsName/Description
      if (categoryID && !selectedCategory) return;

      // Fresh wizard flow: create a new job
      jobCreatedRef.current = true;

      console.log('[ImportProgressStep] dealConfig:', dealConfig);

      const request: CreateJobRequest = {
        fileID,
        mappingConfig: mappingConfig as MappingConfig,
        dealMappingsName: dealConfig?.dealMappingsName || undefined,
        dealMappingsDescription:
          dealConfig?.dealMappingsDescription || undefined,
      };

      // Include save config if name is set
      if (saveConfigName) {
        request.saveConfig = { name: saveConfigName };
      }

      leadImportService
        .createJob(request)
        .then((data) => {
          if (data.jobID) {
            setJobID(data.jobID);
          }
        })
        .catch((err) => {
          console.error('[ImportProgressStep] Failed to create job:', err);
        });
    }
  }, [selectedCategory]);

  // Poll job status
  const {
    jobStatus: polledStatus,
    isPolling,
    error: pollingError,
  } = useJobPolling(jobID);

  // When job reaches terminal status, advance to results
  const hasAdvancedRef = useRef(false);
  useEffect(() => {
    if (!polledStatus || hasAdvancedRef.current) return;

    const { status } = polledStatus;
    if (!isPollingEnabled(status)) {
      // Terminal status reached
      hasAdvancedRef.current = true;
      setJobStatus(polledStatus);
      setStep('results');
    }
  }, [polledStatus, setJobStatus, setStep]);

  // Derive progress values
  const totalRows = polledStatus?.totalRows ?? 0;
  const processedRows = polledStatus?.processedRows ?? 0;
  const successfulRows = polledStatus?.successfulRows ?? 0;
  const failedRows = polledStatus?.failedRows ?? 0;
  const progress = calcProgress(processedRows, totalRows);

  return (
    <Box data-testid="import-progress-step">
      <Typography variant="h6" gutterBottom>
        Importing Leads
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 2 }}
        aria-live="polite"
      >
        {isPolling
          ? 'Your import is in progress. Please do not close this page.'
          : polledStatus
          ? 'Import complete.'
          : 'Starting import...'}
      </Typography>

      {/* Progress bar */}
      <Box sx={{ mb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Progress
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {progress}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          aria-label="Import progress"
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </Box>

      {/* Live counters */}
      <ProgressStats
        successfulRows={successfulRows}
        failedRows={failedRows}
        processedRows={processedRows}
        totalRows={totalRows}
      />

      {/* Polling timeout error */}
      {pollingError && !isNotFoundError(pollingError) && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {pollingError.message}
        </Alert>
      )}

      {/* Job not found during polling (404) */}
      {pollingError && isNotFoundError(pollingError) && (
        <Alert
          severity="warning"
          sx={{ mt: 2 }}
          data-testid="polling-not-found-error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setStep('upload')}
            >
              Back to Upload
            </Button>
          }
        >
          Import job not found. It may have been deleted. Please start a new
          import.
        </Alert>
      )}
    </Box>
  );
};

export default ImportProgressStep;
